/*
Copyright 2025 TekNix Corporation

This software is proprietary and confidential to TekNix Corporation.
All rights reserved. No part of this software may be reproduced, distributed,
or transmitted in any form or by any means, including photocopying, recording,
or other electronic or mechanical methods, without the prior written permission
of TekNix Corporation, except in the case of brief quotations embodied in
critical reviews and certain other noncommercial uses permitted by copyright law.

For permission requests, write to TekNix Corporation at the address below:
TekNix Corporation
Legal Department

UNAUTHORIZED REPRODUCTION OR DISTRIBUTION IS STRICTLY PROHIBITED.
*/

import type { Optional } from '../../@types/common';
import type { IEvent, XHubChatEventEvent } from '../../@types/event';
import type { Membership } from '../../@types/membership';
import type { CachedReceiptStructure, Receipt, ReceiptContent } from '../../@types/read_receipts';
import type { NotificationCount, RoomEventHandlerMap } from '../../@types/room';
import type { RoomStateEvent } from '../../@types/room-state';
import type { XHubChatClient } from '../../client';
import type { RoomData } from '../../sync/interface';
import type { BeaconEvent } from '../beacon/beacon';
import type { IAddLiveEventOptions } from '../event/event-timeline-set';

import type { RoomMember } from './room-member';
import type { Hero } from './room-summary';
import { PendingEventOrdering } from '../../@types/client';
import { EventStatus, EventType, UNSIGNED_THREAD_ID_FIELD } from '../../@types/event';
import { KnownMembership } from '../../@types/membership';
import { ReceiptType } from '../../@types/read_receipts';
import { ALLOWED_TRANSITIONS, NotificationCountType, RoomCategory, RoomEvent } from '../../@types/room';
import { TypedReEmitter } from '../../emit/ReEmitter';
import { Method } from '../../http';
import { removeElement } from '../../utils';
import { logger } from '../../utils/logger';
import { compareEventOrdering } from '../event/compare-event-ordering';
import { XHubChatEvent } from '../event/event';
import { EventTimeline } from '../event/event-timeline';
import { DuplicateStrategy, EventTimelineSet } from '../event/event-timeline-set';
import { ReadReceipt, synthesizeReceipt } from '../read-receipt';
import { RelationsContainer } from '../relations/relations-container';
// eslint-disable-next-line sort-imports
import { Thread, THREAD_RELATION_TYPE, ThreadEvent } from '../thread/thread';
import { RoomReceipts } from './room-receipts';
import { RoomSummary } from './room-summary';

export type RoomEmittedEvents
  = | RoomEvent
    | RoomStateEvent.Events
    | RoomStateEvent.Members
    | RoomStateEvent.NewMember
    | RoomStateEvent.Update
    | RoomStateEvent.Marker
    | XHubChatEventEvent.BeforeRedaction
    | ThreadEvent.New
    | ThreadEvent.Update
    | ThreadEvent.NewReply
    | ThreadEvent.Delete
    | BeaconEvent.New
    | BeaconEvent.Update
    | BeaconEvent.Destroy
    | BeaconEvent.LivenessChange;

export enum RoomNameType {
  EmptyRoom,
  Generated,
  Actual,
}

export type EmptyRoomNameState = {
  type: RoomNameType.EmptyRoom;
  oldName?: string;
};

export type GeneratedRoomNameState = {
  type: RoomNameType.Generated;
  subtype?: 'Inviting';
  names: string[];
  count: number;
};

export type ActualRoomNameState = {
  type: RoomNameType.Actual;
  name: string;
};

export type RoomNameState = EmptyRoomNameState | GeneratedRoomNameState | ActualRoomNameState;

type IOpts = {
  /**
   * Controls where pending messages appear in a room's timeline.
   * If "<b>chronological</b>", messages will appear in the timeline when the call to `sendEvent` was made.
   * If "<b>detached</b>", pending messages will appear in a separate list,
   * accessible via {@link Room#getPendingEvents}.
   * Default: "chronological".
   */
  pendingEventOrdering?: PendingEventOrdering;
  lazyLoadMembers?: boolean;
  /**
   * Set to true to enable improved timeline support.
   */
  timelineSupport?: boolean;
};

export class Room extends ReadReceipt<RoomEmittedEvents, RoomEventHandlerMap> {
  public readonly reEmitter: TypedReEmitter<RoomEmittedEvents, RoomEventHandlerMap>;
  public readonly cachedThreadReadReceipts = new Map<string, CachedReceiptStructure[]>();

  private readonly pendingEventList?: XHubChatEvent[];
  private txnToEvent: Map<string, XHubChatEvent> = new Map(); // Pending in-flight requests { string: XHubChatEvent }

  private bumpStamp: number | undefined = undefined;
  public readonly relations;
  private notificationCounts: NotificationCount = {};
  /**
   * The latest receipts (synthetic and real) for each user in each thread
   * (and unthreaded).
   */
  private roomReceipts = new RoomReceipts(this);
  private selfMembership?: Membership;

  /**
   * The human-readable display name for this room.
   */
  public name: string;
  private readonly timelineSets: EventTimelineSet[];

  /**
   * The room summary.
   */
  public summary: RoomSummary | null = null;

  private membersPromise?: Promise<boolean>;

  /**
   * A `Hero` is a stripped `m.room.member` event which contains the important render-able fields from the event.
   *
   * It is used in MSC4186 (Simplified Sliding Sync) as a replacement for the old `summary` field.
   *
   * When we are doing old-style (`/v3/sync`) sync, we simulate the SSS behaviour by constructing
   * a `Hero` object based on the user id we get from the summary. Obviously, in that case,
   * the `Hero` will lack a `displayName` or `avatarUrl`.
   */
  private heroes: Hero[] | null = null;

  /**
   * The un-homoglyphed name for this room.
   */
  public normalizedName: string;

  /**
   * A mapping of eventId to all visibility changes to apply
   * to the event, by chronological order, as per
   * https://github.com/matrix-org/matrix-doc/pull/3531
   *
   * # Invariants
   *
   * - within each list, all events are classed by
   *   chronological order;
   * - all events are events such that
   *  `asVisibilityEvent()` returns a non-null `IVisibilityChange`;
   * - within each list with key `eventId`, all events
   *   are in relation to `eventId`.
   *
   * @experimental
   */
  private visibilityEvents = new Map<string, XHubChatEvent[]>();

  /**
   * A collection of events known by the client
   * This is not a comprehensive list of the threads that exist in this room
   */
  private threads = new Map<string, Thread>();
  private threadsReady = false;
  /**
   * Empty array if the timeline sets have not been initialized. After initialization:
   * 0: All threads
   * 1: Threads the current user has participated in
   */
  public readonly threadsTimelineSets: [] | [EventTimelineSet, EventTimelineSet] = [];
  /**
   * A record of the latest unthread receipts per user
   * This is useful in determining whether a user has read a thread or not
   */
  private unthreadedReceipts = new Map<string, Receipt>();
  // Useful to know at what point the current user has started using threads in this room
  private oldestThreadedReceiptTs = Infinity;
  private readonly threadNotifications = new Map<string, NotificationCount>();

  /**
   * Construct a new Room.
   *
   * <p>For a room, we store an ordered sequence of timelines, which may or may not
   * be continuous. Each timeline lists a series of events, as well as tracking
   * the room state at the start and the end of the timeline. It also tracks
   * forward and backward pagination tokens, as well as containing links to the
   * next timeline in the sequence.
   *
   * <p>There is one special timeline - the 'live' timeline, which represents the
   * timeline to which events are being added in real-time as they are received
   * from the /sync API. Note that you should not retain references to this
   * timeline - even if it is the current timeline right now, it may not remain
   * so if the server gives us a timeline gap in /sync.
   *
   * <p>In order that we can find events from their ids later, we also maintain a
   * map from event_id to timeline and index.
   *
   * @param roomId - Required. The ID of this room.
   * @param client - Required. The client, used to lazy load members.
   * @param myUserId - Required. The ID of the syncing user.
   * @param roomData - Full data from Backend server about this room.
   * @param opts - Configuration options
   */
  public constructor(
    public readonly roomId: string,
    public readonly client: XHubChatClient,
    public readonly myUserId: string,
    public roomData: RoomData,
    private readonly opts: IOpts = {},
  ) {
    super();

    // In some cases, we add listeners for every displayed XhubChat event, so it's
    // common to have quite a few more than the default limit.
    this.setMaxListeners(100);
    this.reEmitter = new TypedReEmitter(this);
    this.name = roomData?.category === RoomCategory.ChatRoom ? roomData.name : roomData.title;
    this.normalizedName = this.name;
    this.relations = new RelationsContainer(this.client, this);

    opts.pendingEventOrdering = opts.pendingEventOrdering || PendingEventOrdering.Chronological;

    this.timelineSets = [new EventTimelineSet(this, opts)];

    this.reEmitter.reEmit(this.getUnfilteredTimelineSet(), [RoomEvent.Timeline, RoomEvent.TimelineReset]);

    if (this.opts.pendingEventOrdering === PendingEventOrdering.Detached) {
      this.pendingEventList = [];
      this.client.store.getPendingEvents(this.roomId).then((events) => {
        const mapper = this.client.getEventMapper();
        events.forEach(async (serializedEvent: Partial<IEvent>) => {
          const event = mapper(serializedEvent);
          await client.decryptEventIfNeeded(event);
          event.setStatus(EventStatus.NOT_SENT);
          this.addPendingEvent(event, event.getTxnId()!);
        });
      });
    }

    // awaited by getEncryptionTargetMembers while room members are loading
    if (!this.opts.lazyLoadMembers) {
      this.membersPromise = Promise.resolve(false);
    } else {
      this.membersPromise = undefined;
    }
  }

  /**
   * Determine which timeline(s) a given event should live in
   * Thread roots live in both the main timeline and their corresponding thread timeline
   * Relations, replies to thread relation events live only in the thread timeline
   * Relations (other than m.thread), replies to a thread root live only in the main timeline
   * Relations, replies where the parent cannot be found live in no timelines but should be aggregated regardless.
   * Otherwise, the event lives in the main timeline only.
   *
   * Note: when a redaction is applied, the redacted event, events relating
   * to it, and the redaction event itself, will all move to the main thread.
   * This method classifies them as inside the thread of the redacted event.
   * They are moved later as part of makeRedacted.
   * This will change if MSC3389 is merged.
   */
  public eventShouldLiveIn(event: XHubChatEvent, events?: XHubChatEvent[], roots?: Set<string>): { shouldLiveInRoom: boolean; shouldLiveInThread: boolean; threadId?: string } {
    if (!this.client?.supportsThreads()) {
      return {
        shouldLiveInRoom: true,
        shouldLiveInThread: false,
      };
    }

    // A thread root is the only event shown in both timelines
    if (event.isThreadRoot || roots?.has(event.getId()!)) {
      return {
        shouldLiveInRoom: true,
        shouldLiveInThread: true,
        threadId: event.getId(),
      };
    }

    const isThreadRelation = event.isRelation(THREAD_RELATION_TYPE.name);
    const parentEventId = event.getAssociatedId();
    const threadRootId = event.threadRootId;

    // Where the parent is the thread root and this is a non-thread relation this should live only in the main timeline
    if (!!parentEventId && !isThreadRelation && (threadRootId === parentEventId || roots?.has(parentEventId!))) {
      return {
        shouldLiveInRoom: true,
        shouldLiveInThread: false,
      };
    }

    let parentEvent: XHubChatEvent | undefined;
    if (parentEventId) {
      parentEvent = this.findEventById(parentEventId) ?? events?.find(e => e.getId() === parentEventId);
    }

    // Treat non-thread-relations, reactions, and replies as extensions of their parents so evaluate parentEvent instead
    if (parentEvent && !isThreadRelation) {
      return this.eventShouldLiveIn(parentEvent, events, roots);
    }

    // A thread relation (1st and 2nd order) is always only shown in a thread
    if (threadRootId !== undefined) {
      return {
        shouldLiveInRoom: false,
        shouldLiveInThread: true,
        threadId: threadRootId,
      };
    }

    // Due to replies not being typical relations and being used as fallbacks for threads relations
    // If we bypass the if case above then we know we are not a thread, so if we are still a reply
    // then we know that we must be in the main timeline. Same goes if we have no associated parent event.
    if (!parentEventId || !!event.replyEventId) {
      return {
        shouldLiveInRoom: true,
        shouldLiveInThread: false,
      };
    }

    // We've exhausted all scenarios,
    // we cannot assume that it lives in the main timeline as this may be a relation for an unknown thread
    // adding the event in the wrong timeline causes stuck notifications and can break ability to send read receipts
    return {
      shouldLiveInRoom: false,
      shouldLiveInThread: false,
    };
  }

  /**
   * Indicate to the server that the user has entered or left the room.
   */
  public async goIntoRoom(): Promise<void> {
    if (this.roomData.category === RoomCategory.ChatRoom) {
      await this.client.http.authedRequest(
        Method.Get,
        `/chat-rooms/check-last-seen-room?is_in_room=true&room_chat_id=${this.roomId}`,
        undefined,
      );
      this.setUnreadNotificationCount(NotificationCountType.Total, 0);
    } else {
      try {
        if (!this.roomData.notification_id) {
          logger.warn(`No notification_id found for room ${this.roomId}, cannot mark as read.`);
        }

        if (this.roomData.notification_id
          && (this.getUnreadNotificationCount(NotificationCountType.Total) > 0 || !this.roomData.is_read)) {
          await this.client.http.authedRequest(
            Method.Post,
            `/posts/notifications/read`,
            undefined,
            { notification_id: this.roomData.notification_id },
          );
          this.updateRoomData({ ...this.roomData, is_read: true });

          // Store the updated room data
          this.client.store.storeRoom(this);
          this.client.store.save(true);

          this.setUnreadNotificationCount(NotificationCountType.Total, 0);
        }
      } catch (error) {
        logger.error(error);
      }
    }
  }

  /**
   * Set the bump stamp for this room. This can be used for sorting rooms when the timeline
   * entries are unknown. Used in MSC4186: Simplified Sliding Sync.
   * @param bumpStamp The bump_stamp value from the server
   */
  public setBumpStamp(bumpStamp: number): void {
    this.bumpStamp = bumpStamp;
  }

  /**
   * Check whether the pending event list contains a given event by ID.
   * If pending event ordering is not "detached" then this returns false.
   *
   * @param eventId - The event ID to check for.
   */
  public hasPendingEvent(eventId: string): boolean {
    return this.pendingEventList?.some(event => event.getId() === eventId) ?? false;
  }

  /**
   * Get the bump stamp for this room. This can be used for sorting rooms when the timeline
   * entries are unknown. Used in MSC4186: Simplified Sliding Sync.
   * @returns The bump stamp for the room, if it exists.
   */
  public getBumpStamp(): number | undefined {
    return this.bumpStamp;
  }

  /**
   * Get the most recent content event in the room (Post Room)
   * @returns The most recent content event, or null if there are no content events.
   */
  public getContentEvent(): XHubChatEvent | null {
    if (this.getCategory() === RoomCategory.ChatRoom) {
      return null;
    }
    let ev;
    const timeline = this.getLiveTimeline();
    for (let i = timeline.getEvents().length - 1; i >= 0; i--) {
      ev = timeline.getEvents()[i];
      if (ev.isContentMessage) {
        return ev;
      }
    }
    return null;
  }

  /**
   * Indicate to the server that the user has entered or left the room.
   */
  public goOutRoom(): void {
    if (this.roomData.category === 'chat_room') {
      this.client.http.authedRequest(
        Method.Get,
        `/chat-rooms/check-last-seen-room?is_in_room=false&room_chat_id=${this.roomId}`,
        undefined,
      );
    }
  }

  /**
   * Get a list of members with given membership state.
   * @param membership - The membership state.
   * @returns A list of members with the given membership state.
   */
  public getMembersWithMembership(membership: Membership): RoomMember[] {
    return this.getLiveTimeline().getState(EventTimeline.FORWARDS)?.getMembers().filter((m) => {
      return m.membership === membership;
    }) || [];
  }

  /**
   * Get a list of members whose membership state is "join".
   * @returns A list of currently joined members.
   */
  public getJoinedMembers(): RoomMember[] {
    return this.getMembersWithMembership(KnownMembership.Join);
  }

  public partitionThreadedEvents(
    events: XHubChatEvent[],
  ): [timelineEvents: XHubChatEvent[], threadedEvents: XHubChatEvent[], unknownRelations: XHubChatEvent[]] {
    // Indices to the events array, for readability
    const ROOM = 0;
    const THREAD = 1;
    const UNKNOWN_RELATION = 2;
    if (this.client.supportsThreads()) {
      const threadRoots = this.findThreadRoots(events);
      return events.reduce<[XHubChatEvent[], XHubChatEvent[], XHubChatEvent[]]>(
        (memo, event: XHubChatEvent) => {
          const { shouldLiveInRoom, shouldLiveInThread, threadId } = this.eventShouldLiveIn(
            event,
            events,
            threadRoots,
          );

          if (shouldLiveInRoom) {
            memo[ROOM].push(event);
          }

          if (shouldLiveInThread) {
            event.setThreadId(threadId ?? '');
            memo[THREAD].push(event);
          }

          if (!shouldLiveInThread && !shouldLiveInRoom) {
            memo[UNKNOWN_RELATION].push(event);
          }

          return memo;
        },
        [[], [], []],
      );
    } else {
      // When `threadSupport` is disabled treat all events as timelineEvents
      return [events as XHubChatEvent[], [] as XHubChatEvent[], [] as XHubChatEvent[]];
    }
  }

  /**
   * Takes the given thread root events and creates threads for them.
   */
  public processThreadRoots(events: XHubChatEvent[], toStartOfTimeline: boolean): void {
    if (!this.client.supportsThreads()) return;

    for (const rootEvent of events) {
      if (this.getLiveTimeline().getState(EventTimeline.FORWARDS) !== undefined) {
        EventTimeline.setEventMetadata(rootEvent, this.getLiveTimeline().getState(EventTimeline.FORWARDS)!, toStartOfTimeline);
      }
      if (!this.getThread(rootEvent.getId()!)) {
        this.createThread(rootEvent.getId()!, rootEvent, [], toStartOfTimeline);
      }
    }
  }

  /**
   * Get an event which is stored in our unfiltered timeline set, or in a thread
   *
   * @param eventId - event ID to look for
   * @returns the given event, or undefined if unknown
   */
  public findEventById(eventId: string): XHubChatEvent | undefined {
    let event = this.getUnfilteredTimelineSet().findEventById(eventId);

    if (!event) {
      const threads = this.getThreads();
      for (let i = 0; i < threads.length; i++) {
        const thread = threads[i];
        event = thread.findEventById(eventId);
        if (event) {
          return event;
        }
      }
    }

    return event;
  }

  /**
   * Returns the most recent unthreaded receipt for a given user
   * @param userId - the MxID of the User
   * @returns an unthreaded Receipt. Can be undefined if receipts have been disabled
   * or a user chooses to use private read receipts (or we have simply not received
   * a receipt from this user yet).
   */
  public getLastUnthreadedReceiptFor(userId: string): Receipt | undefined {
    return this.unthreadedReceipts.get(userId);
  }

  /**
   * Used to aggregate the local echo for a relation, and also
   * for re-applying a relation after it's redaction has been cancelled,
   * as the local echo for the redaction of the relation would have
   * un-aggregated the relation. Note that this is different from regular messages,
   * which are just kept detached for their local echo.
   *
   * Also note that live events are aggregated in the live EventTimelineSet.
   * @param event - the relation event that needs to be aggregated.
   */
  private aggregateNonLiveRelation(event: XHubChatEvent): void {
    this.relations.aggregateChildEvent(event);
  }

  /**
   * The live event timeline for this room, with the oldest event at index 0.
   *
   * @deprecated Present for backwards compatibility.
   *             Use getLiveTimeline().getEvents() instead
   */
  public get timeline(): XHubChatEvent[] {
    return this.getLiveTimeline().getEvents();
  }

  /**
   * Determines if the given user has read a particular event ID with the known
   * history of the room. This is not a definitive check as it relies only on
   * what is available to the room at the time of execution.
   *
   * @param userId - The user ID to check the read state of.
   * @param eventId - The event ID to check if the user read.
   * @returns true if the user has read the event, false otherwise.
   */
  public hasUserReadEvent(userId: string, eventId: string): boolean {
    return this.roomReceipts.hasUserReadEvent(userId, eventId);
  }

  /**
   * Get one of the notification counts for this room
   * @param type - The type of notification count to get. default: 'total'
   * @returns The notification count, or undefined if there is no count
   *                  for this type.
   */
  public getUnreadNotificationCount(type = NotificationCountType.Total): number {
    const count = this.getRoomUnreadNotificationCount(type);
    return count;
  }

  /**
   * Get one of the notification counts for this room
   * @param type - The type of notification count to get. default: 'total'
   * @returns The notification count, or undefined if there is no count
   *                  for this type.
   */
  public getRoomUnreadNotificationCount(type = NotificationCountType.Total): number {
    return this.notificationCounts[type] ?? 0;
  }

  /**
   * Get a specific event from the pending event list, if configured, null otherwise.
   *
   * @param eventId - The event ID to check for.
   */
  public getPendingEvent(eventId: string): XHubChatEvent | null {
    return this.pendingEventList?.find(event => event.getId() === eventId) ?? null;
  }

  /**
   * Find when a client has gained thread capabilities by inspecting the oldest
   * threaded receipt
   * @returns the timestamp of the oldest threaded receipt
   */
  public getOldestThreadedReceiptTs(): number {
    return this.oldestThreadedReceiptTs;
  }

  /**
   * Get the instance of the thread associated with the current event
   * @param eventId - the ID of the current event
   * @returns a thread instance if known
   */
  public getThread(eventId: string): Thread | null {
    return this.threads.get(eventId) ?? null;
  }

  /**
   * Get all the known threads in the room
   */
  public getThreads(): Thread[] {
    return Array.from(this.threads.values());
  }

  /**
   * Create a new thread in this room
   * @param threadId - the ID of the thread to create
   * @param rootEvent - the root event of the thread
   * @param events - initial events to add to the thread
   * @param toStartOfTimeline - whether to add events to the start of the timeline
   * @returns the created `Thread`
   */
  public createThread(
    threadId: string,
    rootEvent: XHubChatEvent | undefined,
    events: XHubChatEvent[] = [],
    toStartOfTimeline: boolean,
  ): Thread {
    if (this.threads.has(threadId)) {
      return this.threads.get(threadId)!;
    }

    if (rootEvent) {
      const relatedEvents = this.relations.getAllChildEventsForEvent(rootEvent.getId()!);
      if (relatedEvents?.length) {
        events = events.concat(relatedEvents);
      }
    }

    const thread = new Thread(threadId, rootEvent, {
      room: this,
      client: this.client,
      pendingEventOrdering: this.opts.pendingEventOrdering,
      receipts: this.cachedThreadReadReceipts.get(threadId) ?? [],
    });

    // Add the re-emitter before we start adding events to the thread so we don't miss events
    this.reEmitter.reEmit(thread, [
      RoomEvent.Timeline,
      RoomEvent.TimelineReset,
    ]);

    // All read receipts should now come down from sync, we do not need to keep
    // a reference to the cached receipts anymore.
    this.cachedThreadReadReceipts.delete(threadId);

    // If we managed to create a thread and figure out its `id` then we can use it
    // This has to happen before thread.addEvents, because that adds events to the event-timeline, and the
    // event-timeline sometimes looks up thread information via the room.
    this.threads.set(thread.id, thread);

    // This is necessary to be able to jump to events in threads:
    // If we jump to an event in a thread where neither the event, nor the root,
    // nor any thread event are loaded yet, we'll load the event as well as the thread root, create the thread,
    // and pass the event through this.
    thread.addEvents(events, false);

    // We need to update the thread root events, but the thread may not be ready yet.
    // If it isn't, it will fire ThreadEvent.Update when it is and we'll call updateThreadRootEvents then.
    if (this.threadsReady && thread.initialEventsFetched) {
      this.updateThreadRootEvents(thread, toStartOfTimeline, false);
    }
    this.emit(ThreadEvent.New, thread, toStartOfTimeline);

    return thread;
  }

  private updateThreadRootEvents = (thread: Thread, toStartOfTimeline: boolean, recreateEvent: boolean): void => {
    if (thread.length) {
      this.updateThreadRootEvent(this.threadsTimelineSets?.[0], thread, toStartOfTimeline, recreateEvent);
      if (thread.hasCurrentUserParticipated) {
        this.updateThreadRootEvent(this.threadsTimelineSets?.[1], thread, toStartOfTimeline, recreateEvent);
      }
    }
  };

  private updateThreadRootEvent = (
    timelineSet: Optional<EventTimelineSet>,
    thread: Thread,
    toStartOfTimeline: boolean,
    recreateEvent: boolean,
  ): void => {
    if (timelineSet && thread.rootEvent) {
      if (recreateEvent) {
        timelineSet.removeEvent(thread.id);
      }
      if (Thread.hasServerSideSupport) {
        timelineSet.addLiveEvent(thread.rootEvent, {
          duplicateStrategy: DuplicateStrategy.Replace,
          fromCache: false,
          roomState: this.getLiveTimeline().getState(EventTimeline.FORWARDS),
          addToState: false,
        });
      } else {
        timelineSet.addEventToTimeline(thread.rootEvent, timelineSet.getLiveTimeline(), {
          toStartOfTimeline,
          addToState: false,
        });
      }
    }
  };

  /**
   * Set one of the notification counts for this room
   * @param type - The type of notification count to set.
   * @param count - The new count
   */
  public setUnreadNotificationCount(type: NotificationCountType, count: number): void {
    this.notificationCounts[type] = count;
    this.emit(RoomEvent.UnreadNotifications, this.notificationCounts);
  }

  public setUnread(type: NotificationCountType, count: number): void {
    return this.setUnreadNotificationCount(type, count);
  }

  /**
   * Get the live unfiltered timeline for this room.
   *
   * @returns live timeline
   */
  public getLiveTimeline(): EventTimeline {
    return this.getUnfilteredTimelineSet().getLiveTimeline();
  }

  /**
   * Get the category of this room
   * @returns The category of the room
   */
  public getCategory(): RoomCategory {
    return this.roomData.category;
  }

  /**
   * Get the list of pending sent events for this room
   *
   * @returns A list of the sent events
   * waiting for remote echo.
   *
   * @throws If `opts.pendingEventOrdering` was not 'detached'
   */
  public getPendingEvents(): XHubChatEvent[] {
    if (!this.pendingEventList) {
      throw new Error(
        `Cannot call getPendingEvents with pendingEventOrdering == ${this.opts.pendingEventOrdering}`,
      );
    }
    return this.pendingEventList;
  }

  /**
   * Helper to return the main unfiltered timeline set for this room
   * @returns room's unfiltered timeline set
   */
  public getUnfilteredTimelineSet(): EventTimelineSet {
    return this.timelineSets[0];
  }

  /**
   * Persists all pending events to local storage
   *
   * If the current room is encrypted only encrypted events will be persisted
   * all messages that are not yet encrypted will be discarded
   */
  private savePendingEvents(): void {
    if (this.pendingEventList) {
      const pendingEvents = this.pendingEventList
        .map((event) => {
          return {
            ...event.event,
            txn_id: event.getTxnId(),
          };
        });
      this.client.store.setPendingEvents(this.roomId, pendingEvents);
    }
  }

  /**
   * Get a member from the current room state.
   * @param userId - The user ID of the member.
   * @returns The member or `null`.
   */
  public getMember(userId: string): RoomMember | null {
    return this.getLiveTimeline().getState(EventTimeline.FORWARDS)?.getMember(userId) || null;
  }

  /**
   * Removes a pending event for this room
   *
   * @returns True if an element was removed.
   */
  public removePendingEvent(eventId: string): boolean {
    if (!this.pendingEventList) {
      throw new Error(`Cannot call removePendingEvent with pendingEventOrdering == ${this.opts.pendingEventOrdering}`);
    }
    const removed = removeElement(this.pendingEventList, ev => ev.getId() === eventId, false);
    this.savePendingEvents();
    return removed;
  }

  /**
   * Assert that all timeline sets in this room still have live timelines.
   */
  private assertTimelineSetsAreLive(): void {
    for (let i = 0; i < this.timelineSets.length; i++) {
      const liveTimeline = this.timelineSets[i].getLiveTimeline();
      if (liveTimeline.getPaginationToken(EventTimeline.FORWARDS)) {
        throw new Error(
          `live timeline ${i} is no longer live - it has a pagination token (${liveTimeline.getPaginationToken(EventTimeline.FORWARDS)})`,
        );
      }
      if (liveTimeline.getNeighbouringTimeline(EventTimeline.FORWARDS)) {
        throw new Error(`live timeline ${i} is no longer live - it has a neighbouring timeline`);
      }
    }
  }

  /**
   * Deal with the echo of a message we sent.
   *
   * <p>We move the event to the live timeline if it isn't there already, and
   * update it.
   *
   * @param remoteEvent -   The event received from
   *    /sync
   * @param localEvent -    The local echo, which
   *    should be either in the pendingEventList or the timeline.
   *
   * @internal
   *
   * @remarks
   * Fires {@link RoomEvent.LocalEchoUpdated}
   */
  public handleRemoteEcho(remoteEvent: XHubChatEvent, localEvent: XHubChatEvent): void {
    const oldEventId = localEvent.getId()!;
    const newEventId = remoteEvent.getId()!;
    const oldStatus = localEvent.status;

    logger.debug(`Got remote echo for event ${oldEventId} -> ${newEventId} old status ${oldStatus}`);
    // no longer pending
    this.txnToEvent.delete(remoteEvent.getUnsigned().transaction_id!);

    // if it's in the pending list, remove it
    if (this.pendingEventList) {
      this.removePendingEvent(oldEventId);
    }

    // replace the event source (this will preserve the plaintext payload if
    // any, which is good, because we don't want to try decoding it again).
    localEvent.handleRemoteEcho(remoteEvent.event);

    const { shouldLiveInRoom, threadId } = this.eventShouldLiveIn(remoteEvent);
    const thread = threadId ? this.getThread(threadId) : null;
    thread?.setEventMetadata(localEvent);
    thread?.timelineSet.handleRemoteEcho(localEvent, oldEventId, newEventId);

    if (shouldLiveInRoom) {
      for (const timelineSet of this.timelineSets) {
        // if it's already in the timeline, update the timeline map. If it's not, add it.
        timelineSet.handleRemoteEcho(localEvent, oldEventId, newEventId);
      }
    }

    // XXX: New in this package
    // Special case: if this is a redaction, we need to update the redacted event too
    // Because reaction don't have their own event ID, we use. pattern post/comment + _post_like
    // When redact a reaction, we need to find the parent event and update ID to remove this ID,
    // for don't conflict when like again
    if (remoteEvent.isRedaction()) {
      const parentEventId = remoteEvent.getAssociatedId();
      if (parentEventId) {
        const parentEvent = this.findEventById(parentEventId);

        const newEventId = `${parentEventId}-redacted-${new Date().getTime()}`;
        remoteEvent.event.content && (remoteEvent.event.content.redacts = newEventId);
        remoteEvent.event.redacts = newEventId;
        remoteEvent.updateId(`${remoteEvent.getId()!}-redacted-${new Date().getTime()}`);

        parentEvent?.updateId(newEventId);
      }
    }

    this.emit(RoomEvent.LocalEchoUpdated, localEvent, this, oldEventId, oldStatus);
  }

  /**
   * Return the timeline sets for this room.
   * @returns array of timeline sets for this room
   */
  public getTimelineSets(): EventTimelineSet[] {
    return this.timelineSets;
  }

  private tryApplyRedaction = (event: XHubChatEvent): void => {
    // FIXME: apply redactions to notification list

    // NB: We continue to add the redaction event to the timeline at the
    // end of this function so clients can say "so and so redacted an event"
    // if they wish to. Also this may be needed to trigger an update.

    if (event.isRedaction()) {
      const redactId = event.event.redacts;

      // if we know about this event, redact its contents now.
      const redactedEvent = redactId ? this.findEventById(redactId) : undefined;

      if (redactedEvent) {
        this.applyEventAsRedaction(event, redactedEvent);
      }
    } else if (event.getType() === EventType.RoomMember) {
      const membership = event.getContent().membership;
      if (
        membership !== KnownMembership.Ban
        && !(membership === KnownMembership.Leave && event.getStateKey() !== event.getSender())
      ) {
        // Not a ban or kick, therefore not a membership event we care about here.
        return;
      }
      const redactEvents = event.getContent()['org.matrix.msc4293.redact_events'];
      if (redactEvents !== true) {
        // Invalid or not set - nothing to redact.
        return;
      }

      // The redaction is possible, so let's find all the events and apply it.
      const events = this.getTimelineSets()
        .map(s => s.getTimelines())
        .reduce((p, c) => {
          p.push(...c);
          return p;
        }, [])
        .map(t => t.getEvents().filter(e => e.getSender() === event.getStateKey()))
        .reduce((p, c) => {
          p.push(...c);
          return c;
        }, []);
      for (const toRedact of events) {
        this.applyEventAsRedaction(event, toRedact);
      }
    }
  };

  /**
   * Applies an event as a redaction of another event, regardless of whether the redacting
   * event is actually a redaction.
   *
   * Callers should use tryApplyRedaction instead.
   *
   * @param redactionEvent The event which redacts an event.
   * @param redactedEvent The event being redacted.
   * @private
   */
  private applyEventAsRedaction(redactionEvent: XHubChatEvent, redactedEvent: XHubChatEvent): void {
    const threadRootId = redactedEvent.threadRootId;
    redactedEvent.makeRedacted(redactionEvent, this);

    // If this is in the current state, replace it with the redacted version
    if (redactedEvent.isState()) {
      const currentStateEvent = this.getLiveTimeline().getState(EventTimeline.FORWARDS)?.getStateEvents(
        redactedEvent.getType(),
        redactedEvent.getStateKey()!,
      );
      if (currentStateEvent?.getId() === redactedEvent.getId()) {
        this.getLiveTimeline().getState(EventTimeline.FORWARDS)?.setStateEvents([redactedEvent]);
      }
    }

    this.emit(RoomEvent.Redaction, redactionEvent, this, threadRootId);

    // TODO: we stash user displaynames (among other things) in
    // RoomMember objects which are then attached to other events
    // (in the sender and target fields). We should get those
    // RoomMember objects to update themselves when the events that
    // they are based on are changed.

    // Remove any visibility change on this event.
    this.visibilityEvents.delete(redactedEvent.getId()!);

    // If this event is a visibility change event, remove it from the
    // list of visibility changes and update any event affected by it.
    if (redactedEvent.isVisibilityEvent()) {
      this.redactVisibilityChangeEvent(redactionEvent);
    }
  }

  private redactVisibilityChangeEvent(event: XHubChatEvent): void {
    // Sanity checks.
    if (!event.isVisibilityEvent) {
      throw new Error('expected a visibility change event');
    }
    const relation = event.getRelation();
    const originalEventId = relation?.event_id;
    const visibilityEventsOnOriginalEvent = this.visibilityEvents.get(originalEventId!);
    if (!visibilityEventsOnOriginalEvent) {
      // No visibility changes on the original event.
      // In particular, this change event was not recorded,
      // most likely because it was ill-formed.
      return;
    }
    const index = visibilityEventsOnOriginalEvent.findIndex(change => change.getId() === event.getId());
    if (index === -1) {
      // This change event was not recorded, most likely because
      // it was ill-formed.
      return;
    }
    // Remove visibility change.
    visibilityEventsOnOriginalEvent.splice(index, 1);

    // If we removed the latest visibility change event, propagate changes.
    if (index === visibilityEventsOnOriginalEvent.length) {
      const originalEvent = this.findEventById(originalEventId!);
      if (!originalEvent) {
        return;
      }
      if (index === 0) {
        // We have just removed the only visibility change event.
        this.visibilityEvents.delete(originalEventId!);
        originalEvent.applyVisibilityEvent();
      } else {
        const newEvent = visibilityEventsOnOriginalEvent[visibilityEventsOnOriginalEvent.length - 1];
        const newVisibility = newEvent.asVisibilityChange();
        if (!newVisibility) {
          // Event is ill-formed.
          // This breaks our invariant.
          throw new Error('at this stage, visibility changes should be well-formed');
        }
        originalEvent.applyVisibilityEvent(newVisibility);
      }
    }
  }

  private processLiveEvent(event: XHubChatEvent): void {
    this.tryApplyRedaction(event);

    // Sliding Sync modifications:
    // The proxy cannot guarantee every sent event will have a transaction_id field, so we need
    // to check the event ID against the list of pending events if there is no transaction ID
    // field. Only do this for events sent by us though as it's potentially expensive to loop
    // the pending events map.
    const txnId = event.getUnsigned().transaction_id;
    if (!txnId && event.getSender() === this.myUserId) {
      // check the txn map for a matching event ID
      for (const [tid, localEvent] of this.txnToEvent) {
        if (localEvent.getId() === event.getId()) {
          logger.debug('processLiveEvent: found sent event without txn ID: ', tid, event.getId());
          // update the unsigned field so we can re-use the same code paths
          const unsigned = event.getUnsigned();
          unsigned.transaction_id = tid;
          event.setUnsigned(unsigned);
          break;
        }
      }
    }
  }

  /**
   * Add events to a timeline
   *
   * <p>Will fire "Room.timeline" for each event added.
   *
   * @param events - A list of events to add.
   * @param toStartOfTimeline -   True to add these events to the start
   * (oldest) instead of the end (newest) of the timeline. If true, the oldest
   * event will be the <b>last</b> element of 'events'.
   * @param addToState -  True to update the room state with state events
   * @param timeline - timeline to add events to.
   * @param paginationToken - token for the next batch of events
   * @remarks
   * Fires {@link RoomEvent.Timeline}
   */
  public addEventsToTimeline(events: XHubChatEvent[], toStartOfTimeline: boolean, addToState: boolean, timeline: EventTimeline, paginationToken?: string): void {
    timeline.getTimelineSet().addEventsToTimeline(events, toStartOfTimeline, addToState, timeline, paginationToken);
  }

  /**
   * Adds/handles ephemeral events such as typing notifications and read receipts.
   * @param events - A list of events to process
   */
  public addEphemeralEvents(events: XHubChatEvent[]): void {
    for (const event of events) {
      if (event.getType() === EventType.Typing) {
        this.getLiveTimeline().getState(EventTimeline.FORWARDS)?.setTypingEvent(event);
      } else if (event.getType() === EventType.Receipt) {
        this.addReceipt(event);
      } // else ignore - life is too short for us to care about these events
    }
  }

  /**
   * Update the room data for this room.
   * @param roomData Full data from Backend server about this room.
   * @returns The updated room data. - New room data after update.
   */
  public updateRoomData(roomData: RoomData): RoomData {
    this.roomData = { ...this.roomData, ...roomData };
    return this.roomData;
  }

  /**
   * Get the room data for this room.
   * @returns The room data.
   */
  getRoomData(): RoomData {
    return this.roomData;
  }

  /**
   * Add a pending outgoing event to this room.
   *
   * <p>The event is added to either the pendingEventList, or the live timeline,
   * depending on the setting of opts.pendingEventOrdering.
   *
   * <p>This is an internal method, intended for use by MatrixClient.
   *
   * @param event - The event to add.
   *
   * @param txnId - Transaction id for this outgoing event
   *
   * @throws if the event doesn't have status SENDING, or we aren't given a
   * unique transaction id.
   *
   * @remarks
   * Fires {@link RoomEvent.LocalEchoUpdated}
   */
  public addPendingEvent(event: XHubChatEvent, txnId: string): void {
    if (event.status !== EventStatus.SENDING && event.status !== EventStatus.NOT_SENT) {
      throw new Error(`addPendingEvent called on an event with status ${event.status}`);
    }

    if (this.txnToEvent.get(txnId)) {
      throw new Error(`addPendingEvent called on an event with known txnId ${txnId}`);
    }

    // call setEventMetadata to set up event.sender etc
    // as event is shared over all timelineSets, we set up its metadata based
    // on the unfiltered timelineSet.
    EventTimeline.setEventMetadata(event, this.getLiveTimeline().getState(EventTimeline.FORWARDS)!, false);

    this.txnToEvent.set(txnId, event);
    if (this.pendingEventList) {
      if (this.pendingEventList.some(e => e.status === EventStatus.NOT_SENT)) {
        logger.warn('Setting event as NOT_SENT due to messages in the same state');
        event.setStatus(EventStatus.NOT_SENT);
      }
      this.pendingEventList.push(event);
      this.savePendingEvents();

      if (event.isRelation()) {
        // For pending events, add them to the relations collection immediately.
        // (The alternate case below already covers this as part of adding to
        // the timeline set.)
        this.aggregateNonLiveRelation(event);
      }

      if (event.isRedaction()) {
        const redactId = event.event.redacts;
        let redactedEvent = this.pendingEventList.find(e => e.getId() === redactId);
        if (!redactedEvent && redactId) {
          redactedEvent = this.findEventById(redactId);
        }
        if (redactedEvent) {
          redactedEvent.markLocallyRedacted(event);
          this.emit(RoomEvent.Redaction, event, this, redactedEvent.threadRootId);
        }
      }
    } else {
      for (const timelineSet of this.timelineSets) {
        if (timelineSet.getFilter()) {
          if (timelineSet.getFilter()!.filterRoomTimeline([event]).length) {
            timelineSet.addEventToTimeline(event, timelineSet.getLiveTimeline(), {
              toStartOfTimeline: false,
              addToState: false, // We don't support localEcho of state events yet
            });
          }
        } else {
          timelineSet.addEventToTimeline(event, timelineSet.getLiveTimeline(), {
            toStartOfTimeline: false,
            addToState: false, // We don't support localEcho of state events yet
          });
        }
      }
    }

    this.emit(RoomEvent.LocalEchoUpdated, event, this);
  }

  /**
   * Add an event to the end of this room's live timelines. Will fire
   * "Room.timeline".
   *
   * @param event - Event to be added
   * @param addLiveEventOptions - addLiveEvent options
   * @internal
   *
   * @remarks
   * Fires {@link RoomEvent.Timeline}
   */
  private addLiveEvent(event: XHubChatEvent, addLiveEventOptions: IAddLiveEventOptions): void {
    const { duplicateStrategy, timelineWasEmpty, fromCache, addToState } = addLiveEventOptions;

    // Add to our timeline sets
    for (const timelineSet of this.timelineSets) {
      timelineSet.addLiveEvent(event, {
        duplicateStrategy,
        fromCache,
        timelineWasEmpty,
        addToState,
      });
    }

    this.addReceipt(synthesizeReceipt(event.sender?.userId ?? '', event, ReceiptType.Read), true);
  }

  /**
   * Given an event, find the thread it belongs to
   * @param event - the event to find the thread for
   * @returns the thread the event belongs to, or null if it does not belong to any thread
   */
  public findThreadForEvent(event?: XHubChatEvent): Thread | null {
    if (!event) return null;

    const { threadId } = this.eventShouldLiveIn(event);
    return threadId ? this.getThread(threadId) : null;
  }

  /**
   * Given some events, find the IDs of all the thread roots that are referred to by them.
   */
  private findThreadRoots(events: XHubChatEvent[]): Set<string> {
    const threadRoots = new Set<string>();
    for (const event of events) {
      const threadRootId = event.threadRootId;
      if (threadRootId !== undefined) {
        threadRoots.add(threadRootId);
      }
    }
    return threadRoots;
  }

  /**
   * Add some events to this room. This can include state events, message
   * events and typing notifications. These events are treated as "live" so
   * they will go to the end of the timeline.
   *
   * @param events - A list of events to add.
   * @param addLiveEventOptions - addLiveEvent options
   * @throws If `duplicateStrategy` is not falsy, 'replace' or 'ignore'.
   */
  public async addLiveEvents(events: XHubChatEvent[], addLiveEventOptions: IAddLiveEventOptions): Promise<void> {
    const { duplicateStrategy, fromCache, timelineWasEmpty = false, addToState } = addLiveEventOptions;
    if (duplicateStrategy && !['replace', 'ignore'].includes(duplicateStrategy)) {
      throw new Error('duplicateStrategy MUST be either \'replace\' or \'ignore\'');
    }

    // sanity check that the live timeline is still live
    this.assertTimelineSetsAreLive();

    const options: IAddLiveEventOptions = { duplicateStrategy, fromCache, timelineWasEmpty, addToState };

    const threadRoots = this.findThreadRoots(events);
    const eventsByThread: { [threadId: string]: XHubChatEvent[] } = {};

    // List of extra events to check for being parents of any relations encountered
    const neighbouringEvents = [...events];

    for (const event of events) {
      // TODO: We should have a filter to say "only add state event types X Y Z to the timeline".
      this.processLiveEvent(event);

      // if event have transaction_id, see if we have a local echo for it
      if (event.getUnsigned().transaction_id) {
        const existingEvent = this.txnToEvent.get(event.getUnsigned().transaction_id!);
        if (existingEvent) {
          // remote echo of an event we sent earlier
          this.handleRemoteEcho(event, existingEvent);
          continue; // we can skip adding the event to the timeline sets, it is already there
        }
      }

      let {
        shouldLiveInRoom,
        shouldLiveInThread,
        threadId = '',
      } = this.eventShouldLiveIn(event, neighbouringEvents, threadRoots);

      // if event is relation but don't find this parent event in the current events,
      // try to fetch it from the server, and re-evaluate where the event should live
      if (!shouldLiveInThread && !shouldLiveInRoom && event.isRelation()) {
        try {
          const parentEvent = new XHubChatEvent(
            await this.client.fetchRoomEvent(this.roomData.category, event.relationEventId!),
          );
          neighbouringEvents.push(parentEvent);
          if (parentEvent.threadRootId) {
            threadRoots.add(parentEvent.threadRootId);
            const unsigned = event.getUnsigned();
            unsigned[UNSIGNED_THREAD_ID_FIELD.name] = parentEvent.threadRootId;
            event.setUnsigned(unsigned);
          }

          ({
            shouldLiveInRoom,
            shouldLiveInThread,
            threadId = '',
          } = this.eventShouldLiveIn(event, neighbouringEvents, threadRoots));
        } catch (e) {
          logger.error('Failed to load parent event of unhandled relation', e);
        }
      }

      if (shouldLiveInThread && !eventsByThread[threadId]) {
        eventsByThread[threadId] = [];
      }

      eventsByThread[threadId]?.push(event);

      if (shouldLiveInRoom) {
        this.addLiveEvent(event, options);
      } else if (!shouldLiveInThread && event.isRelation()) {
        this.relations.aggregateChildEvent(event);
      }
    }

    Object.entries(eventsByThread).forEach(([threadId, threadEvents]) => {
      this.addThreadedEvents(threadId, threadEvents, false);
    });
  }

  private addThreadedEvents(threadId: string, events: XHubChatEvent[], toStartOfTimeline = false): void {
    const thread = this.getThread(threadId);
    if (thread) {
      thread.addEvents(events, toStartOfTimeline);
    } else {
      const rootEvent = this.findEventById(threadId) ?? events.find(e => e.getId() === threadId);
      this.createThread(threadId, rootEvent, events, toStartOfTimeline);
    }
  }

  /**
   * Determine the order of two events in this room.
   *
   * In principle this should use the same order as the server, but in practice
   * this is difficult for events that were not received over the Sync API. See
   * MSC4033 for details.
   *
   * This implementation leans on the order of events within their timelines, and
   * falls back to comparing event timestamps when they are in different
   * timelines.
   *
   * See https://github.com/matrix-org/matrix-js-sdk/issues/3325 for where we are
   * tracking the work to fix this.
   *
   * @param leftEventId - the id of the first event
   * @param rightEventId - the id of the second event
   * @returns -1 if left \< right, 1 if left \> right, 0 if left == right, null if
   *          we can't tell (because we can't find the events).
   */
  public compareEventOrdering(leftEventId: string, rightEventId: string): number | null {
    return compareEventOrdering(this, leftEventId, rightEventId);
  }

  /**
   * Sets the membership this room was received as during sync
   * @param membership - join | leave | invite
   */
  public updateMyMembership(membership: Membership): void {
    const prevMembership = this.selfMembership;
    this.selfMembership = membership;
    if (prevMembership !== membership) {
      this.emit(RoomEvent.MyMembership, this, membership, prevMembership);
    }
  }

  /**
   * Format a message event for display in room summary.
   * Handles different message types with appropriate formatting.
   *
   * @param event - The message event to format
   * @returns formatted message text
   */
  private formatMessageForSummary(event: XHubChatEvent): string {
    const content = event.getContent();
    let messageText = '';
    switch (content.msgtype) {
      case 'm.text':
        messageText = content.text || '';
        break;
      case 'm.image':
        messageText = 'sent an image';
        break;
      case 'm.file':
        messageText = 'sent a file';
        break;
      case 'm.audio':
        messageText = 'sent an audio message';
        break;
      case 'm.video':
        messageText = 'sent a video';
        break;
      case 'm.location':
        messageText = 'shared a location';
        break;
      default:
        messageText = 'sent a message';
    }

    // Truncate long messages for summary
    if (content.msgtype === 'm.text' && messageText.length > 100) {
      messageText = `${messageText.substring(0, 97)}...`;
    }
    return `${messageText}`;
  }

  /**
   * Get the latest message content for room summary description.
   * Searches through the live timeline events to find the most recent message.
   *
   * @returns formatted message content for summary, or empty string if no messages found
   */
  private getLatestMessageForSummary(): string {
    const timeline = this.getLiveTimeline();
    const events = timeline.getEvents();

    // Find the most recent message event (not state events)
    for (let i = events.length - 1; i >= 0; i--) {
      const event = events[i];
      if (event.getType() === EventType.RoomMessage) {
        return this.formatMessageForSummary(event);
      }
    }
    return ''; // No messages found
  }

  /**
   * Get the timeline which contains the given event from the unfiltered set, if any
   *
   * @param eventId -  event ID to look for
   * @returns timeline containing
   * the given event, or null if unknown
   */
  public getTimelineForEvent(eventId: string): EventTimeline | null {
    // const event = this.findEventById(eventId);
    return this.getUnfilteredTimelineSet().getTimelineForEvent(eventId);
  }

  /**
   * Removes a single event from this room.
   * @param eventId -  The id of the event to remove
   *
   * @returns true if the event was removed from any of the room's timeline sets
   */
  public removeEvent(eventId: string): boolean {
    let removedAny = false;
    for (const timelineSet of this.timelineSets) {
      const removed = timelineSet.removeEvent(eventId);
      if (removed) removedAny = true;
    }
    return removedAny;
  }

  /**
   * Set one of the notification count for a thread
   * @param threadId - the root event ID
   * @param type - The type of notification count to get. default: 'total'
   *  The notification count, or undefined if there is no count
   */
  public setThreadUnreadNotificationCount(threadId: string, type: NotificationCountType, count: number): void {
    const notification: NotificationCount = {
      highlight: this.threadNotifications.get(threadId)?.highlight,
      total: this.threadNotifications.get(threadId)?.total,
      ...{
        [type]: count,
      },
    };

    this.threadNotifications.set(threadId, notification);

    this.emit(RoomEvent.UnreadNotifications, notification, threadId);
  }

  /**
   * Update the status / event id on a pending event, to reflect its transmission
   * progress.
   *
   * <p>This is an internal method.
   *
   * @param event -      local echo event
   * @param newStatus -  status to assign
   * @param newEventId -      new event id to assign. Ignored unless newStatus == EventStatus.SENT.
   *
   * @remarks
   * Fires {@link RoomEvent.LocalEchoUpdated}
   */
  public updatePendingEvent(event: XHubChatEvent, newStatus: EventStatus, newEventId?: string): void {
    logger.log(
      `setting pendingEvent status to ${newStatus} in ${event.getRoomId()} `
      + `event ID ${event.getId()} -> ${newEventId}`,
    );

    // if the message was sent, we expect an event id
    if (newStatus === EventStatus.SENT && !newEventId) {
      throw new Error('updatePendingEvent called with status=SENT, but no new event id');
    }

    // SENT races against with socket, so we have to special-case it.
    if (newStatus === EventStatus.SENT) {
      // Check if we already have this event in the timeline
      const timeline = this.getTimelineForEvent(newEventId!);
      if (timeline) {
        // we've already received the event via the event stream.
        // nothing more to do here, assuming the transaction ID was correctly matched.
        // Let's check that.
        const remoteEvent = this.findEventById(newEventId!);
        const remoteTxnId = remoteEvent?.getUnsigned().transaction_id;
        if (!remoteTxnId && remoteEvent) {
          // This code path is mostly relevant for the Sliding Sync proxy.
          // The remote event did not contain a transaction ID, so we did not handle
          // the remote echo yet. Handle it now.
          const unsigned = remoteEvent.getUnsigned();
          unsigned.transaction_id = event.getTxnId();
          remoteEvent.setUnsigned(unsigned);
          // the remote event is _already_ in the timeline, so we need to remove it so
          // we can convert the local event into the final event.
          this.removeEvent(remoteEvent.getId()!);
          this.handleRemoteEcho(remoteEvent, event);
        }

        return;
      }
    }

    const oldStatus = event.status;
    const oldEventId = event.getId()!;

    if (!oldStatus) {
      throw new Error('updatePendingEventStatus called on an event which is not a local echo.');
    }

    const allowed = ALLOWED_TRANSITIONS[oldStatus];
    if (!allowed?.includes(newStatus)) {
      throw new Error(`Invalid EventStatus transition ${oldStatus}->${newStatus}`);
    }

    event.setStatus(newStatus);

    if (newStatus === EventStatus.SENT) {
      // update the event id
      event.replaceLocalEventId(newEventId!);

      const { shouldLiveInRoom } = this.eventShouldLiveIn(event);
      if (shouldLiveInRoom) {
        // if the event was already in the timeline (which will be the case if
        // opts.pendingEventOrdering==chronological), we need to update the
        // timeline map.
        for (const timelineSet of this.timelineSets) {
          timelineSet.replaceEventId(oldEventId, newEventId!);
        }
      }
    } else if (newStatus === EventStatus.CANCELLED) {
      // remove it from the pending event list, or the timeline.
      if (this.pendingEventList) {
        this.removePendingEvent(oldEventId);
      }
      this.removeEvent(oldEventId);
    }
    this.savePendingEvents();

    this.emit(RoomEvent.LocalEchoUpdated, event, this, oldEventId, oldStatus);
  }

  /**
   * Recalculate various aspects of the room, including the room name and
   * room summary. Call this any time the room's current state is modified.
   * May fire "Room.name" if the room name is updated.
   *
   * @remarks
   * Fires {@link RoomEvent.Name}
   */
  public recalculate(): void {
    // set fake stripped state events if this is an invite room so logic remains
    // consistent elsewhere.
    const membershipEvent = this.getLiveTimeline().getState(EventTimeline.FORWARDS)?.getStateEvents(EventType.RoomMember, this.myUserId);
    if (membershipEvent) {
      const membership = membershipEvent.getContent().membership;
      this.updateMyMembership(membership!);
    }

    // Get the latest message for summary description
    this.summary = new RoomSummary(this.roomId, {
      title: this.name,
      desc: this.roomData.description || this.roomData.content,
    });
  }

  /**
   * Get the avatar url for the room, if one was set.
   * @returns the avatar url or falsy
   */
  public getAvatarUrl(): string | null {
    return this.getLiveTimeline().getState(EventTimeline.FORWARDS)?.getStateEvents(EventType.RoomAvatar, '')?.getContent()?.url || null;
  }

  /**
   * Add a receipt event to the room.
   * @param event - The m.receipt event.
   * @param synthetic - True if this event is implicit.
   *                  - True if this event created by the client, false if it came from the server.
   *                  - Default: false
   */
  public addReceipt(event: XHubChatEvent, synthetic = false): void {
    const content = event.getContent<ReceiptContent>();
    this.roomReceipts.add(content, synthetic);
    // send events after we've regenerated the structure & cache, otherwise things that
    // listened for the event would read stale data.
    this.emit(RoomEvent.Receipt, event, this);
  }

  /**
   * This issue should also be addressed on synapse's side and is tracked as part
   * of https://github.com/matrix-org/synapse/issues/14837
   *
   *
   * We consider a room  fully read if the current user has sent
   * the last event in the live timeline of that context and if the read receipt
   * we have on record matches.
   * This also detects all unread threads and applies the same logic to those
   * contexts
   */
  public fixupNotifications(userId: string): void {
    super.fixupNotifications(userId);
  }
}
